import { Component } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { StorageFactory } from '@payments/list-manager';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap, take } from 'rxjs/operators';
import { Role } from '../../../services/roles/roles.models';
import { RolesService } from '../../../services/roles/roles.service';
import { ModalService } from '../../../widgets/modal/modal.service';
import { objectValidator } from '../../../widgets/form-utils/validators';
import { AuthorizationService } from '../../authorization/authorization.service';

interface Company {
    id: number;
    name: string;
    roles: Role[];
}

@Component({
    selector: 'payments-change-roles',
    templateUrl: './change-roles.component.html',
    standalone: false
})
export class ChangeRolesComponent {
    companiesControl = new UntypedFormControl(null, objectValidator);
    rolesControl = new UntypedFormControl(null, objectValidator);
    companies: Company[];
    filteredCompanies$: Observable<Company[]>;
    filteredRoles$: Observable<Role[]>;
    loading = false;
    submitted = false;

    errorStateMatcher: ErrorStateMatcher = {
        isErrorState: (control: AbstractControl | null) => !!(control && control.invalid && this.submitted),
    };

    constructor(
        private modalService: ModalService,
        private authorizationService: AuthorizationService,
        private rolesService: RolesService
    ) {
        combineLatest([
            this.authorizationService.availableRoles$.pipe(take(1)),
            this.authorizationService.activeRole$.pipe(take(1), catchError(() => of<Role | undefined>(undefined))),
        ]).subscribe(([availableRoles, activeRole]) => {
            this.companies = availableRoles.reduce(function(companies: Company[], role: Role) {
                const company = companies.find((c) => c && c.id === role.companyId);
                if (company) {
                    company.roles.push(role);
                } else {
                    companies.push({ id: role.companyId, name: role.companyName, roles: [role] });
                }
                return companies;
            }, []);

            const matchedCompany = activeRole
                ? this.companies.find((c) => c.id === activeRole.companyId)
                : undefined;

            if (activeRole && matchedCompany) {
                this.companiesControl.setValue(matchedCompany);
                const matchedRole = matchedCompany.roles.find((r) => r.id === activeRole.id);
                if (matchedRole) {
                    this.rolesControl.setValue(matchedRole);
                }
            } else if (this.companies.length === 1) {
                this.companiesControl.setValue(this.companies[0]);
                if (this.companies[0].roles.length === 1) {
                    this.rolesControl.setValue(this.companies[0].roles[0]);
                }
            }
        });

        this.filteredCompanies$ = this.companiesControl.valueChanges.pipe(
            startWith(this.companiesControl.value),
            map((value) => this.filterCompanies(value))
        );

        this.filteredRoles$ = combineLatest([
            this.companiesControl.valueChanges.pipe(startWith(this.companiesControl.value)),
            this.rolesControl.valueChanges.pipe(startWith(this.rolesControl.value))
        ]).pipe(map(([, value]) => this.filterRoles(value)));

        this.companiesControl.valueChanges.subscribe((value) => {
            if (value && typeof value !== 'string' && value.roles.length === 1) {
                this.rolesControl.setValue(value.roles[0]);
            }
        });
    }

    submit() {
        this.submitted = true;
        this.companiesControl.markAsTouched();
        this.rolesControl.markAsTouched();
        if (this.companiesControl.invalid || this.rolesControl.invalid) {
            return;
        }

        this.loading = true;
        const role = this.rolesControl.value;
        this.rolesService.selectRole(role.id).pipe(
            switchMap(() => this.authorizationService.refreshSession())
        ).subscribe(() => {
            StorageFactory.sessionStorage.clear();
            StorageFactory.urlStorage.clear();
            window.location.href = '/';
        });
    }

    close(): void {
        this.modalService.closeAll();
    }

    displayCompany(company: Company): string {
        return company ? company.name : '';
    }

    displayRole(role: Role): string {
        return role ? role.roleName : '';
    }

    clearCompany() {
        this.companiesControl.reset();
        this.clearRole();
    }

    clearRole() {
        this.rolesControl.reset();
    }

    hasCompany(): boolean {
        const value = this.companiesControl.value;
        return value != null && value !== '';
    }

    hasRole(): boolean {
        const value = this.rolesControl.value;
        return value != null && value !== '';
    }

    private filterCompanies(value: string | Company): Company[] {
        if (!value) {
            return this.companies;
        }

        if (typeof value === 'string') {
            const filterValue = this.removeDiacritics(value.toLowerCase());

            return this.companies.filter((c) => {
                return this.removeDiacritics(c.name.toLowerCase()).indexOf(filterValue) !== -1;
            });
        } else {
            return this.companies.filter((c) => c.id === value.id);
        }
    }

    private filterRoles(value: string | Role): Role[] {
        if (!this.companiesControl.value || typeof this.companiesControl.value === 'string') {
            return [];
        }
        const selectedCompany: Company = this.companiesControl.value;
        if (!value) {
            return selectedCompany.roles;
        }

        if (typeof value === 'string') {
            const filterValue = this.removeDiacritics(value.toLowerCase());

            return selectedCompany.roles.filter((r) => {
                return this.removeDiacritics(r.roleName.toLowerCase()).indexOf(filterValue) !== -1;
            });
        } else {
            return selectedCompany.roles.filter((r) => r.id === value.id);
        }
    }

    private removeDiacritics(str: string) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
}
